<script setup lang="ts">
import getReferences from "@/graphql/references.query.graphql";
import type {
  BaseImageFragment,
  GetReferencesQuery,
  GetReferencesQueryVariables,
} from "@/types/graphql";

// composables
const { locale, d } = useI18n();
const route = useRoute();

// Set component vars
/**
 * Fliter model an share global state
 */
const filterModel = ref<string[]>([]);
// set filter if route params are set
filterModel.value = route.query.filter
  ? Array.isArray(route.query.filter)
    ? route.query.filter
    : [route.query.filter]
  : [];
// build object for graphql filter
const filters = computed(() => {
  if (!filterModel.value.length) return undefined;
  return { categories: { name: { in: filterModel.value } } };
});

// set data fetching
/**
 * Fetch all Refereces
 * Map the result object to the component props
 */
const { result } = useQuery<GetReferencesQuery, GetReferencesQueryVariables>(
  getReferences,
  {
    locale: locale.value,
    filters: filters.value,
  },
);

// parse response for blog articles
const references = computed(() =>
  result.value?.references?.data.reduce<
    {
      id: string;
      headline: string;
      subheadline: string;
      url: string;
      image: BaseImageFragment;
    }[]
  >((validatedReferences, references) => {
    if (!references.attributes) return validatedReferences;
    const { id } = references;
    const { title, createdAt, slug, thumbnail } = references.attributes;
    if (title && createdAt && slug && id)
      validatedReferences.push({
        id: id,
        headline: title,
        subheadline: d(references.attributes?.createdAt),
        url: route.fullPath + "/" + slug,
        image: thumbnail.data?.attributes,
      });
    return validatedReferences;
  }, []),
);
</script>

<template>
  <section class="reference-list outer-space container pb-10 pt-5.5 lg:p-5.5">
    <div class="mt-5 grid place-items-center gap-5 md:grid-cols-2">
      <nuxt-link
        v-for="(reference, index) in references"
        :key="`${reference.id}-${index}`"
        :to="reference.url"
        class="relative grid aspect-square h-full w-full overflow-hidden bg-integer-black font-pixel text-h2 text-integer-white"
      >
        <base-image
          class="reference-thumbnail aspect-square max-h-full w-full max-w-full object-cover"
          v-bind="reference.image"
          sizes="(min-width: 768px) 50vw, (max-width: 767px) 100vw"
          :eager-loading="index < 2 ? true : false"
        />
        <span
          ref="title"
          class="reference-title absolute w-full place-self-center text-center"
        >
          {{ reference.headline }}
        </span>
      </nuxt-link>
    </div>
  </section>
</template>

<style scoped>
.reference-thumbnail {
  transition: opacity 300ms ease;
  opacity: 100%;
}
.reference-title {
  transition: opacity 300ms ease;
  opacity: 0;
}

a:hover .reference-thumbnail {
  opacity: 40%;
}
a:hover .reference-title {
  opacity: 100%;
}
</style>
